import { GridColDef } from "@mui/x-data-grid-pro";
import {
  Card,
  CheckIcon,
  Divider,
  DocumentIcon,
  FormControlLabel,
  GKButton,
  GKCheckbox,
  GKClientDataGrid,
  GKModal,
  GKTextField,
  Grid2,
  InputAdornment,
  ReloadIcon,
  RupeeIcon,
  Show,
  StatusField,
  Tab,
  Tabs,
  Typography,
} from "components";
import { useMutation } from "hooks/useQueries";
import { AllocationDetails } from "interfaces/ClientBasket";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "store/store";
import {
  moneyCommaSeparator,
  moneyCommaSeparatorPoint,
  numberToCurrency,
} from "utils";
import axiosInstance from "utils/axiosInstance";
import { GKModalProps } from "utils/commonInterface";
import { extraColumnsField } from "utils/helpers";

interface Props {
  basketDetails: {
    portfolioId: number;
    basketId: number;
    version: number;
    companyLogo: string;
  };
}

const column: GridColDef[] = [
  {
    ...extraColumnsField("ASSETS NAME", "company", 250, 1),
  },
  {
    ...extraColumnsField("TRANS. TYPE", "transaction type", 120, 1),
    align: "center",
    headerAlign: "center",
    renderCell: (params) => {
      return <StatusField statusString={params.row["transaction type"]} />;
    },
  },
  {
    ...extraColumnsField("PRICE (₹)", "cmp", 120, 1),
    type: "number",
    valueFormatter: (params) => moneyCommaSeparator(params.value),
    valueGetter: (params) => params?.row?.cmp || "-",
  },
  {
    ...extraColumnsField("QTY", "quantity", 100, 1),
    type: "number",
    valueFormatter: (params) => moneyCommaSeparatorPoint(params.value),
    valueGetter: (params) => params.row.quantity || "0",
  },
  {
    ...extraColumnsField("TOTAL (₹)", "amount", 150, 1),
    type: "number",
    valueFormatter: (params) => moneyCommaSeparator(params.value),
    valueGetter: (params) => params?.row?.amount || "-",
  },
];

const GenerateTradeModal = (props: GKModalProps & Props) => {
  const { open, setOpen, basketDetails } = props;

  const navigate = useNavigate();

  const userData = useAppSelector((state) => state?.user?.userData);

  const [tab, setTab] = useState(0);
  const [generateTrade, setGenerateTrade] = useState(false);
  const [data, setData] = useState({ cash: 0, minInvestedAmount: false });
  const [residualAmount, setResidualAmount] = useState(0);

  const {
    data: allocationDetails,
    mutate,
    isLoading,
  } = useMutation({
    mutationFn: (response) =>
      axiosInstance.post(
        `/model_portfolio/${basketDetails.basketId}/allocation/`,
        response,
      ),
    onSuccess: (response) => {
      setGenerateTrade(true);
      setResidualAmount(
        data.cash -
          response?.data[0]?.plan.reduce(
            (sum: number, item: any) => sum + item.amount,
            0,
          ),
      );
    },
  });

  const { mutate: createOrder, isLoading: createLoading } = useMutation({
    mutationFn: (data: any) =>
      axiosInstance.post(
        `/model_portfolio/${basketDetails.basketId}/order-manager/`,
        data,
      ),
    onSuccess: (response) => {
      const url = `/broker-integration/order?token=${JSON.parse(localStorage.getItem("token"))}&userPhone=${
        userData?.phone
      }&is_lm=false&order_id=${response.data?.orderAggregatorId}&portfolio_id=${
        basketDetails.portfolioId
      }&logo=${basketDetails?.companyLogo}`;

      navigate(url);
    },
  });

  const response = {
    useCurrentAllocation: true,
    allocType: tab === 0 ? "allocation" : "rebalance",
    allocationBy: "version",
    weightageDistributionMethod: "PRORATA",
    rebalanceUsing: tab === 0 ? "ledgerBalance" : "portfolioValue",
    allocVersion: basketDetails?.version,
    portfolios: [
      {
        id: basketDetails.portfolioId,
        cash: data.cash,
        exitWeightage: 0,
      },
    ],
  };

  return (
    <GKModal
      modalTitle={"Invest Your Money"}
      open={open}
      setOpen={setOpen}
      width={!generateTrade && "500px"}
      maxHeight="calc(100vh - 300px)"
      footer={
        !generateTrade && (
          <GKButton
            variant="contained"
            startIcon={<DocumentIcon />}
            loading={isLoading}
            onClick={() => mutate(response)}
          >
            Generate Trades
          </GKButton>
        )
      }
    >
      <Grid2 container spacing={3}>
        <Show.When isTrue={!generateTrade}>
          <Grid2 size={12}>
            <Tabs
              sx={{
                "& .MuiTabs-flexContainer": {
                  backgroundColor: "var(--white)",
                },
              }}
              onChange={(e, val) => {
                setTab(val);
                setData({ cash: 0, minInvestedAmount: false });
              }}
              value={tab}
            >
              <Tab label={"Allocation"} disableRipple />
              <Tab label={"Rebalance"} disableRipple />
            </Tabs>
            <Divider />
          </Grid2>
          <Grid2 container size={12} spacing={2}>
            <Grid2 size={12}>
              <Typography fontSize={13} fontWeight={500} color="333">
                {tab === 0
                  ? "How much would you like to invest?"
                  : "Do you want to add more funds for Rebalance?"}
              </Typography>
              <GKTextField
                type="number"
                disabled={data.minInvestedAmount}
                value={data.cash}
                onChange={(event) =>
                  setData((prev) => {
                    return {
                      ...prev,
                      cash: +event.target.value,
                    };
                  })
                }
              />
            </Grid2>
            <Show.When isTrue={tab === 0}>
              <Grid2 size={12}>
                <FormControlLabel
                  style={{ marginLeft: 2 }}
                  control={
                    <GKCheckbox
                      disableLeftPadding
                      checked={data.minInvestedAmount}
                      onChange={(event) =>
                        setData((prev) => {
                          return {
                            ...prev,
                            minInvestedAmount: event.target.checked,
                            cash: event.target.checked ? 1000 : 0,
                          };
                        })
                      }
                    />
                  }
                  labelPlacement="end"
                  label={
                    <Typography color="#333">Min. Invested Amount</Typography>
                  }
                />
              </Grid2>
            </Show.When>
          </Grid2>
        </Show.When>

        <Show.When isTrue={generateTrade}>
          <Grid2 size={12}>
            <Card
              sx={{
                p: "16px 24px",
                borderRadius: "4px",
                background: "#F8F8F8",
              }}
            >
              <Grid2 container spacing={2}>
                <Grid2
                  container
                  size={10}
                  spacing={2}
                  display={"flex"}
                  alignItems={"center"}
                >
                  <Grid2 size={6}>
                    <Typography fontSize={13} fontWeight={500} color="333">
                      How much would you like to invest?
                    </Typography>
                    <GKTextField
                      type="number"
                      disabled={data.minInvestedAmount}
                      value={data.cash}
                      onChange={(event) =>
                        setData((prev) => {
                          return {
                            ...prev,
                            cash: +event.target.value,
                          };
                        })
                      }
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <RupeeIcon />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </Grid2>
                  <Grid2 size={6} mt={3}>
                    <GKButton
                      variant="text"
                      startIcon={<ReloadIcon />}
                      loading={isLoading}
                      onClick={() => mutate(response)}
                    >
                      Generate Trade
                    </GKButton>
                  </Grid2>
                  <Grid2 size={12}>
                    <FormControlLabel
                      style={{ marginLeft: 2 }}
                      control={
                        <GKCheckbox
                          disableLeftPadding
                          checked={data.minInvestedAmount}
                          onChange={(event) =>
                            setData((prev) => {
                              return {
                                ...prev,
                                minInvestedAmount: event.target.checked,
                                cash: event.target.checked ? 1000 : 0,
                              };
                            })
                          }
                        />
                      }
                      labelPlacement="end"
                      label={
                        <Typography color="#333">
                          Min. Invested Amount
                        </Typography>
                      }
                    />
                  </Grid2>
                </Grid2>
                <Grid2 container size={2} spacing={2}>
                  <Grid2
                    size={12}
                    display={"flex"}
                    flexDirection={"column"}
                    justifyContent={"center"}
                  >
                    <Typography
                      fontSize={12}
                      fontWeight={400}
                      color="#697586"
                      textAlign={"end"}
                    >
                      Residual Amount
                    </Typography>
                    <Typography
                      fontSize={20}
                      fontWeight={600}
                      textAlign={"end"}
                    >
                      {numberToCurrency(residualAmount)}
                    </Typography>
                  </Grid2>
                </Grid2>
              </Grid2>
            </Card>
          </Grid2>
          <Grid2 size={12}>
            <GKClientDataGrid
              loading={false}
              columns={column}
              rows={
                allocationDetails?.data[0]?.plan.map(
                  (dataItem: AllocationDetails, index: number) => {
                    return {
                      ...dataItem,
                      id: index,
                    };
                  },
                ) || []
              }
              name="generateTradeHolding"
              density="compact"
              headerComponent={
                <GKButton
                  variant="contained"
                  startIcon={<CheckIcon />}
                  loading={createLoading}
                  onClick={() => createOrder(response)}
                >
                  Execute Order
                </GKButton>
              }
            />
          </Grid2>
        </Show.When>
      </Grid2>
    </GKModal>
  );
};

export default GenerateTradeModal;
